/* lost-pet-alerts.js — vanilla progressive enhancement for the Lost Pet Alerts page.
 * Replaces the framework runtime from the React prototype:
 *   1. the AlertNetwork demo (#sn-lost-pet-alerts [data-sn-widget="AlertNetwork"])
 *      — a staged, timer-driven animation: mark missing → community alerted →
 *        found & scanned → you're notified. Faithful to AlertNetwork.jsx's timers.
 * No dependencies. Self-contained. Safe to load with `defer`.
 *
 * The page renders a faithful static snapshot server-side (idle stage = -1); this
 * script rebuilds the widget so it can drive the staged animation. The FAQ
 * accordion is owned by the shared converted/_assets/sn-accordion.js. If the
 * script never runs, the static demo + FAQ questions are still visible.
 */
(function () {
  'use strict';

  // <html lang> comes from request.locale.iso_code, so /de/ pages report "de".
  var SN_DE = typeof document !== 'undefined' && document.documentElement
    ? /^de/i.test(document.documentElement.getAttribute('lang') || '')
    : false;
  var T = function (en, de) { return SN_DE ? de : en; };

  // ---- Widget data (ported verbatim from AlertNetwork.jsx) ------------------
  var PET = 'Bandit';

  var AN_DOTS = [
    { x: 70, y: 86, t: 'owner' }, { x: 150, y: 300, t: 'owner' }, { x: 405, y: 92, t: 'owner' },
    { x: 356, y: 300, t: 'owner' }, { x: 300, y: 56, t: 'owner' }, { x: 110, y: 196, t: 'owner' },
    { x: 420, y: 210, t: 'owner' }, { x: 198, y: 92, t: 'owner' }, { x: 250, y: 312, t: 'owner' },
    { x: 78, y: 300, t: 'vet' }, { x: 412, y: 300, t: 'vet' }, { x: 58, y: 150, t: 'vet' },
    { x: 336, y: 176, t: 'kennel' }, { x: 178, y: 256, t: 'kennel' },
    { x: 312, y: 232, t: 'finder' },
  ];
  var AN_TYPE = {
    owner: { fill: '#CFFF90', stroke: '#224031', icon: null },
    vet: { fill: '#1B4241', stroke: '#1B4241', icon: 'heart' },
    kennel: { fill: '#FFDDBF', stroke: '#224031', icon: null },
    finder: { fill: '#CFFF90', stroke: '#224031', icon: null },
  };
  var AN_STEPS = [
    { k: 'missing', label: T('Marked missing', 'Als vermisst gemeldet'), detail: function (n) { return SN_DE ? n + ' als vermisst gemeldet - Alarm ausgelöst' : n + ' reported lost - alert triggered'; } },
    { k: 'alerted', label: T('Community alerted', 'Community alarmiert'), detail: function () { return T('16 nearby · 12 owners · 3 vets · 1 kennel', '16 in der Nähe · 12 Halter · 3 Tierärzte · 1 Tierpension'); } },
    { k: 'found', label: T('Found & scanned', 'Gefunden & gescannt'), detail: function () { return T('0.4 miles away · finder shared location', '0,6 km entfernt · Finder hat den Standort geteilt'); } },
    { k: 'notified', label: T('You’re notified', 'Sie werden benachrichtigt'), detail: function () { return T('Text sent with location + a way to talk', 'Nachricht mit Standort und Kontaktmöglichkeit gesendet'); } },
  ];

  var SVGNS = 'http://www.w3.org/2000/svg';

  // ---- SVG fragment helpers (ported from illustrations.jsx) -----------------
  function pawMarkSvg(size, color) {
    return '<svg width="' + size + '" height="' + size + '" viewBox="0 0 24 24" fill="' + color + '" aria-hidden="true">' +
      '<ellipse cx="7" cy="9" rx="2" ry="2.6"></ellipse>' +
      '<ellipse cx="12" cy="7.4" rx="2" ry="2.7"></ellipse>' +
      '<ellipse cx="17" cy="9" rx="2" ry="2.6"></ellipse>' +
      '<path d="M12 11.2c2.7 0 4.8 2 4.8 4.2 0 1.8-1.6 2.8-3.3 2.5-.6-.1-1.2-.1-1.5-.1s-.9 0-1.5.1c-1.7.3-3.3-.7-3.3-2.5 0-2.2 2.1-4.2 4.8-4.2Z"></path></svg>';
  }
  var ICON_PATHS = {
    arrow: '<path d="M5 12h14M13 6l6 6-6 6"></path>',
    check: '<path d="M20 6 9 17l-5-5"></path>',
    alert: '<path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z"></path><path d="M12 9v4M12 17h.01"></path>',
  };
  function iconSvg(name, size, color) {
    return '<svg width="' + size + '" height="' + size + '" viewBox="0 0 24 24" fill="none" stroke="' + color +
      '" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' + ICON_PATHS[name] + '</svg>';
  }

  // ---- Tiny DOM helpers -----------------------------------------------------
  function h(tag, opts, kids) {
    var e = document.createElement(tag);
    opts = opts || {};
    if (opts.css) e.style.cssText = opts.css;
    if (opts.cls) e.className = opts.cls;
    if (opts.html != null) e.innerHTML = opts.html;
    if (opts.on) for (var ev in opts.on) e.addEventListener(ev, opts.on[ev]);
    if (opts.attrs) for (var a in opts.attrs) e.setAttribute(a, opts.attrs[a]);
    (kids || []).forEach(function (c) {
      if (c == null) return;
      e.appendChild(typeof c === 'string' ? document.createTextNode(c) : c);
    });
    return e;
  }
  // SVG-namespaced element from a markup string
  function svgEl(tag, attrs) {
    var e = document.createElementNS(SVGNS, tag);
    if (attrs) for (var a in attrs) e.setAttribute(a, attrs[a]);
    return e;
  }
  function svgFromMarkup(markup) {
    // Parse SVG markup into a real <svg> node (innerHTML on a div won't namespace SVG).
    var doc = new DOMParser().parseFromString(markup, 'image/svg+xml');
    return document.importNode(doc.documentElement, true);
  }

  // ---- AlertNetwork widget --------------------------------------------------
  function initAlertNetwork(root) {
    var state = { stage: -1 }; // -1 idle; 0..3 steps
    var timers = [];
    var cx = 240, cy = 184;

    function clearTimers() {
      timers.forEach(function (t) { clearTimeout(t); });
      timers = [];
    }
    function run() {
      clearTimers();
      state.stage = 0;
      render();
      timers.push(setTimeout(function () { state.stage = 1; render(); }, 700));
      timers.push(setTimeout(function () { state.stage = 2; render(); }, 1700));
      timers.push(setTimeout(function () { state.stage = 3; render(); }, 2600));
    }
    function reset() {
      clearTimers();
      state.stage = -1;
      render();
    }

    // ---- SVG map --------------------------------------------------------------
    function dotGroup(d) {
      var ty = AN_TYPE[d.t];
      var r = d.t === 'finder' ? 11 : 9;
      var isFinder = d.t === 'finder';
      var alerted = state.stage >= 1;
      var found = state.stage >= 2;
      var shown = alerted || (isFinder && found);
      var g = svgEl('g');
      if (shown && isFinder && found) {
        g.appendChild(svgEl('circle', { cx: d.x, cy: d.y, r: '20', fill: 'none', stroke: '#224031', 'stroke-width': '2.5' }));
      }
      g.appendChild(svgEl('circle', {
        cx: d.x, cy: d.y, r: String(r),
        fill: shown ? ty.fill : '#fff',
        stroke: shown ? ty.stroke : 'rgba(28,25,23,0.25)',
        'stroke-width': shown ? '2' : '1.5',
      }));
      if (ty.icon && shown && d.t === 'vet') {
        g.appendChild(svgEl('path', {
          d: 'M0-3.2C-1.6-4.8-4-4-4-1.8-4 .3-1 1.2 0 2.6 1-1.2 4 .3 4-1.8 4-4 1.6-4.8 0-3.2Z',
          transform: 'translate(' + d.x + ' ' + d.y + ')', fill: '#CFFF90',
        }));
      }
      if (isFinder && found) {
        g.appendChild(svgEl('path', {
          d: 'M-3 0 -1 2 3 -2', transform: 'translate(' + d.x + ' ' + d.y + ')',
          stroke: '#224031', 'stroke-width': '2.4', fill: 'none',
          'stroke-linecap': 'round', 'stroke-linejoin': 'round',
        }));
      }
      return g;
    }

    function buildSvg() {
      var alerted = state.stage >= 1;
      var found = state.stage >= 2;
      var svg = svgEl('svg', { viewBox: '0 0 480 368', 'aria-label': 'Local alert network' });
      svg.style.cssText = 'width:100%;display:block';

      // faint streets
      var streets = svgEl('g', { stroke: 'rgba(28,25,23,0.07)', 'stroke-width': '10' });
      streets.appendChild(svgEl('path', { d: 'M0 120H480M0 250H480M140 0V368M340 0V368' }));
      svg.appendChild(streets);

      // range rings
      if (alerted) {
        [70, 128, 188].forEach(function (r, i) {
          svg.appendChild(svgEl('circle', {
            cx: cx, cy: cy, r: String(r), fill: 'none', stroke: '#5B8C3E', 'stroke-width': '1.5',
            'stroke-dasharray': '4 8', opacity: String(0.5 - i * 0.12),
          }));
        });
      }
      // line finder → home
      if (found) {
        svg.appendChild(svgEl('line', {
          x1: cx, y1: cy, x2: '312', y2: '232', stroke: '#224031', 'stroke-width': '2',
          'stroke-dasharray': '3 5', opacity: '0.5',
        }));
      }
      // dots
      AN_DOTS.forEach(function (d) { svg.appendChild(dotGroup(d)); });
      // center: your pet
      svg.appendChild(svgEl('circle', { cx: cx, cy: cy, r: '22', fill: '#224031' }));
      if (state.stage >= 0) {
        svg.appendChild(svgEl('circle', { cx: cx, cy: cy, r: '22', fill: 'none', stroke: '#C5462E', 'stroke-width': '3' }));
      }
      var pawG = svgEl('g', { transform: 'translate(' + (cx - 11) + ' ' + (cy - 11) + ')' });
      pawG.appendChild(svgFromMarkup(pawMarkSvg(22, '#CFFF90')));
      svg.appendChild(pawG);
      return svg;
    }

    // ---- Build static shell once; re-render dynamic regions on stage change ---
    var mapHost = h('div', { css: 'background:var(--sn-cream-2);border-radius:20px;padding:14px;position:relative' });
    var legend = h('div', { css: 'display:flex;gap:16px;flex-wrap:wrap;justify-content:center;margin-top:10px;font-size:12px;color:var(--sn-ink-60)' },
      [['#CFFF90', 'Owners'], ['#1B4241', 'Vets'], ['#FFDDBF', 'Kennels'], ['#224031', PET]].map(function (pair) {
        return h('span', { css: 'display:inline-flex;align-items:center;gap:6px' }, [
          h('span', { css: 'width:11px;height:11px;border-radius:999px;background:' + pair[0] + ';border:1px solid rgba(28,25,23,0.2)' }),
          ' ' + pair[1],
        ]);
      }));

    var controls = h('div', { css: 'margin-top:18px;display:flex;gap:12px;align-items:center' });

    var leftCol = h('div', { css: 'padding:30px 34px;display:flex;flex-direction:column' }, [
      h('div', { css: 'margin-bottom:16px' }, [
        h('span', { cls: 'sn-eyebrow', css: 'color:var(--sn-forest)' }, ['Your area · live demo']),
      ]),
      mapHost,
      controls,
    ]);

    // stepper rows
    var stepEls = AN_STEPS.map(function (s, i) {
      var isLast = i === AN_STEPS.length - 1;
      var badge = h('span', { css: 'width:30px;height:30px;border-radius:999px;flex-shrink:0;display:flex;align-items:center;justify-content:center;transition:background .2s' });
      var connector = isLast ? null : h('span', { css: 'width:2px;flex:1;min-height:26px' });
      var col = h('div', { css: 'display:flex;flex-direction:column;align-items:center' }, [badge, connector]);
      var label = h('div', { css: 'font-family:var(--sn-display);font-weight:700;font-size:15.5px' }, [s.label]);
      var detail = h('div', { css: 'font-size:13px;color:var(--sn-ink-60);line-height:1.4' });
      var textWrap = h('div', { css: 'padding-bottom:18px' }, [label, detail]);
      var rowEl = h('div', { css: 'display:flex;gap:14px' }, [col, textWrap]);
      return { row: rowEl, badge: badge, connector: connector, detail: detail, textWrap: textWrap, step: s, index: i, isLast: isLast };
    });
    var stepper = h('div', { css: 'display:flex;flex-direction:column;gap:4px' }, stepEls.map(function (e) { return e.row; }));

    var cardHost = h('div', { css: 'margin-top:auto' });

    var rightCol = h('div', { css: 'background:var(--sn-cream);padding:30px 30px;display:flex;flex-direction:column;border-left:1px solid var(--sn-line)' }, [
      h('h3', { css: 'font-size:19px;margin-bottom:18px' }, ['The first hour, handled']),
      stepper,
      cardHost,
    ]);

    var grid = h('div', {
      css: 'display:grid;grid-template-columns:1.3fr 1fr;border-radius:28px;overflow:hidden;border:1px solid var(--sn-line);box-shadow:0 30px 70px -45px rgba(28,25,23,0.5);background:#fff',
    }, [leftCol, rightCol]);

    function render() {
      var stage = state.stage;

      // map svg
      mapHost.innerHTML = '';
      mapHost.appendChild(buildSvg());
      mapHost.appendChild(legend);

      // controls
      controls.innerHTML = '';
      if (stage === -1) {
        controls.appendChild(h('button', {
          cls: 'sn-btn sn-btn--primary', css: 'font-size:15.5px',
          html: iconSvg('alert', 17, 'currentColor') + (SN_DE ? ' <span>' + PET + ' als vermisst melden</span>' : ' <span>Mark ' + PET + ' missing</span>'),
          on: { click: run },
          attrs: { 'data-an-run': '' },
        }));
      } else {
        controls.appendChild(h('button', {
          cls: 'sn-btn sn-btn--ghost', css: 'font-size:15px',
          on: { click: reset },
          attrs: { 'data-an-reset': '' },
        }, ['Reset demo']));
      }
      // stepper
      stepEls.forEach(function (e) {
        var on = stage >= e.index;
        e.badge.style.background = on ? 'var(--sn-forest)' : '#fff';
        e.badge.style.border = on ? 'none' : '1.5px solid var(--sn-line)';
        e.badge.innerHTML = '';
        if (on) {
          e.badge.appendChild(svgFromMarkup(iconSvg('check', 16, 'var(--sn-lime)')));
        } else {
          e.badge.appendChild(h('span', { css: 'width:7px;height:7px;border-radius:999px;background:var(--sn-line)' }));
        }
        if (e.connector) {
          e.connector.style.background = stage > e.index ? 'var(--sn-forest)' : 'var(--sn-line)';
        }
        e.textWrap.style.opacity = on ? '1' : '0.5';
        e.detail.textContent = on ? e.step.detail(1) : '-';
      });

      // neighbour / found cards
      cardHost.innerHTML = '';
      if (stage >= 1 && stage < 3) {
        var card = h('div', { cls: 'sn-msg-in', css: 'background:#fff;border:1px solid var(--sn-line);border-radius:16px;padding:14px 16px' });
        var head = h('div', { css: 'display:flex;align-items:center;gap:8px;margin-bottom:7px' }, [
          h('span', { css: 'width:24px;height:24px;border-radius:7px;background:var(--sn-forest);display:flex;align-items:center;justify-content:center', html: pawMarkSvg(14, 'var(--sn-lime)') }),
          h('span', { css: 'font-family:var(--sn-display);font-weight:700;font-size:12px' }, [T("A neighbour's phone", 'Das Handy eines Nachbarn')]),
        ]);
        var body = h('div', { css: 'font-size:13.5px;line-height:1.4', html: '<strong>Lost dog near you</strong> - ' + PET + ', last seen Elm Road. Tap if you spot him.' });
        card.appendChild(head);
        card.appendChild(body);
        cardHost.appendChild(card);
      } else if (stage >= 3) {
        var fcard = h('div', { cls: 'sn-msg-in', css: 'background:var(--sn-lime-soft);border:1px solid rgba(34,64,49,0.15);border-radius:16px;padding:16px 18px' });
        var fhead = h('div', { css: 'display:flex;align-items:center;gap:9px;margin-bottom:6px' }, [
          h('span', { css: 'display:inline-flex', html: iconSvg('check', 20, 'var(--sn-forest)') }),
          h('span', { css: 'font-family:var(--sn-display);font-weight:700;font-size:15px;color:var(--sn-forest)' }, [PET + ' is found']),
        ]);
        var fbody = h('p', { css: 'font-size:13.5px;color:var(--sn-ink);line-height:1.45' }, [T("A neighbour scanned his tag 0.4 miles away and shared their location. You've got their number - go bring him home.", 'Ein Nachbar hat seine Marke 0,6 km entfernt gescannt und seinen Standort geteilt. Sie haben jetzt seine Nummer - holen Sie ihn nach Hause.')]);
        fcard.appendChild(fhead);
        fcard.appendChild(fbody);
        cardHost.appendChild(fcard);
      }
    }

    root.innerHTML = '';
    root.appendChild(grid);
    render();
  }

  // The FAQ accordion is handled by the shared converted/_assets/sn-accordion.js.
  // Answers are rendered into the static body.html (for SEO/crawlability), so this
  // file only owns the AlertNetwork widget.

  // ---- Boot -----------------------------------------------------------------
  function boot() {
    var page = document.getElementById('sn-lost-pet-alerts');
    if (!page) return;
    var widget = page.querySelector('[data-sn-widget="AlertNetwork"]');
    if (widget) initAlertNetwork(widget);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
